import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Send, Calculator } from "lucide-react";

const HELP_STEPS = [
  { id: "property", label: "Property" },
  { id: "numbers", label: "Numbers" },
  { id: "review", label: "Review" },
];
const JV_STEPS = [
  { id: "property", label: "Property" },
  { id: "numbers", label: "Numbers" },
  { id: "contract", label: "Contract" },
  { id: "contact", label: "Contact" },
  { id: "review", label: "Review" },
];

export default function SubmitDeal() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    submission_type: params.get("type") === "jv" ? "jv" : "help",
    property_address: "",
    property_type: "",
    beds: "",
    baths: "",
    condition: "",
    asking_price: "",
    arv: "",
    repair_estimate: "",
    assignment_fee: "",
    seller_motivation: "",
    desired_outcome: "",
    closing_date: "",
    contact_name: "",
    contact_phone: "",
    notes: "",
    photos_link: "",
    photo_ids: [],
    doc_ids: [],
  });
  const [photos, setPhotos] = useState([]);
  const [docs, setDocs] = useState([]);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const isJV = form.submission_type === "jv";
  const STEPS = isJV ? JV_STEPS : HELP_STEPS;
  const stepId = STEPS[Math.min(step, STEPS.length - 1)].id;

  const calc = useMemo(() => {
    const arv = Number(form.arv) || 0;
    const repairs = Number(form.repair_estimate) || 0;
    const ask = Number(form.asking_price) || 0;
    const fee = Number(form.assignment_fee) || 0;
    // MAO already accounts for the wholesaler's assignment fee
    const mao = arv > 0 ? arv * 0.7 - repairs - fee : 0;
    const ratio = arv > 0 ? (ask / arv) * 100 : 0;
    const margin = mao - ask;
    const investorProfit = arv > 0 ? arv * 0.3 - fee : 0;
    return { mao, ratio, margin, investorProfit };
  }, [form.arv, form.repair_estimate, form.asking_price, form.assignment_fee]);

  const validate = () => {
    const e = {};
    if (stepId === "property") {
      if (!form.property_address.trim()) e.property_address = "required";
      if (!form.property_type) e.property_type = "required";
    }
    if (stepId === "numbers") {
      if (!form.arv) e.arv = "required";
      if (!form.asking_price) e.asking_price = "required";
      if (!form.repair_estimate) e.repair_estimate = "required";
    }
    if (stepId === "contract") {
      if (docs.length === 0) {
        e.docs = "required";
        toast.error("Upload the contract to submit a JV deal");
      }
    }
    if (stepId === "contact") {
      if (!form.contact_name.trim()) e.contact_name = "required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  useEffect(() => {
    setForm((f) => ({ ...f, photo_ids: photos.map((p) => p.id), doc_ids: docs.map((d) => d.id) }));
  }, [photos, docs]);

  // Prefill from a CRM lead if ?lead_id is passed
  useEffect(() => {
    const leadId = params.get("lead_id");
    if (!leadId) return;
    (async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}`);
        setForm((f) => ({
          ...f,
          submission_type: "jv",
          property_address: data.property_address || f.property_address,
          asking_price: data.asking_price != null ? String(data.asking_price) : f.asking_price,
          seller_motivation: data.motivation || f.seller_motivation,
          contact_name: data.seller_name || f.contact_name,
          contact_phone: data.seller_phone || f.contact_phone,
          notes: data.notes ? `From lead: ${data.notes}` : f.notes,
        }));
        toast.success("Lead data pre-filled", { description: "Fill in the remaining numbers and submit." });
      } catch (e) {
        toast.error("Could not load lead data");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!validate()) return;
    if (busy) return; // guard against double-submit
    setBusy(true);
    try {
      const payload = {
        ...form,
        asking_price: Number(form.asking_price),
        arv: Number(form.arv),
        repair_estimate: Number(form.repair_estimate),
        assignment_fee: form.assignment_fee ? Number(form.assignment_fee) : null,
        closing_date: isJV && form.closing_date ? form.closing_date : null,
      };
      const { data } = await api.post("/deals", payload, { timeout: 45000 });
      toast.success("Deal submitted");
      navigate(`/deals/${data.id}`);
    } catch (e) {
      const msg = e?.code === "ECONNABORTED"
        ? "Submission is taking longer than expected. Try again — your deal may already be saved (check My Deals)."
        : e?.response?.data?.detail || "Submission failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const TYPES = [
    { id: "help", label: "Analyze Property" },
    { id: "jv", label: "Submit Deal for JV" },
  ];

  return (
    <div data-testid="submit-page">
      <div className="mb-6">
        <div className="section-tag mb-3"><span className="dot" /> New submission</div>
        <h1 className="h-display text-4xl">
          {form.submission_type === "jv" ? "Submit Deal for JV" : "Analyze Property"}
        </h1>
      </div>

      {/* Sticky tab + progress group */}
      <div
        className="sticky z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10 pt-2 pb-4 mb-6"
        style={{ top: 64, background: "var(--bg)", borderBottom: "1px solid var(--border)" }}
      >
        {/* Type toggle pills */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-5" data-testid="type-toggle">
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setForm((f) => ({ ...f, submission_type: t.id }));
                const newSteps = t.id === "jv" ? JV_STEPS : HELP_STEPS;
                setStep((s) => Math.min(s, newSteps.length - 1));
              }}
              className="px-3 sm:px-4 py-2 rounded-full text-[11px] sm:text-xs uppercase tracking-wider font-mono transition leading-tight"
              style={{
                background: form.submission_type === t.id ? "var(--lime-glow)" : "var(--surface2)",
                color: form.submission_type === t.id ? "var(--lime)" : "var(--text-muted)",
                border: form.submission_type === t.id ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
              data-testid={`type-pill-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Progress */}
        <div>
          <div className="flex gap-2 mb-3">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className="h-1 flex-1 rounded-full transition"
                style={{ background: i <= step ? "var(--lime)" : "var(--surface3)" }}
              />
            ))}
          </div>
          <div className="flex font-mono text-xs gap-2" style={{ color: "var(--text-muted)" }}>
            {STEPS.map((s, i) => (
              <span
              key={s.id}
              className="flex-1 text-center"
              style={{ color: i === step ? "var(--lime)" : "var(--text-muted)" }}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>
      </div>

      <div className="surface-card p-8 fade-up" key={stepId}>
        {stepId === "property" && (
          <div>
            <h2 className="h-display text-2xl mb-6">Property details</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="field-label">Property Address <span className="req">*</span></label>
                <input
                  className={`input-base ${errors.property_address ? "error" : ""}`}
                  value={form.property_address}
                  onChange={set("property_address")}
                  placeholder="123 Main St, Atlanta, GA 30301"
                  data-testid="address-input"
                />
              </div>
              <div>
                <label className="field-label">Property Type <span className="req">*</span></label>
                <select className={`input-base ${errors.property_type ? "error" : ""}`} value={form.property_type} onChange={set("property_type")} data-testid="property-type-select">
                  <option value="">Select type...</option>
                  <option>Single Family</option>
                  <option>Duplex</option>
                  <option>Multi-Family (3-4 units)</option>
                  <option>Mobile Home</option>
                  <option>Vacant Land</option>
                  <option>Commercial</option>
                </select>
              </div>
              <div>
                <label className="field-label">Condition</label>
                <select className="input-base" value={form.condition} onChange={set("condition")} data-testid="condition-select">
                  <option value="">Select...</option>
                  <option>Light Rehab</option>
                  <option>Medium Rehab</option>
                  <option>Heavy Rehab</option>
                  <option>Full Rehab</option>
                  <option>Turnkey</option>
                </select>
              </div>
              <div>
                <label className="field-label">Beds <span className="opt">(optional)</span></label>
                <input className="input-base" value={form.beds} onChange={set("beds")} data-testid="beds-input" />
              </div>
              <div>
                <label className="field-label">Baths <span className="opt">(optional)</span></label>
                <input className="input-base" value={form.baths} onChange={set("baths")} data-testid="baths-input" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Seller Situation <span className="opt">(motivation, deadline, problem)</span></label>
                <textarea className="input-base" value={form.seller_motivation} onChange={set("seller_motivation")} placeholder="e.g. inherited, behind on taxes, divorce, needs to close in 14 days..." data-testid="motivation-textarea" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Property Photos Link <span className="opt">(optional)</span></label>
                <input
                  type="url"
                  className="input-base"
                  value={form.photos_link}
                  onChange={set("photos_link")}
                  placeholder="Paste Google Drive, Dropbox, or iCloud share link"
                  data-testid="photos-link-input"
                />
                <p className="text-xs mt-1.5 font-mono" style={{ color: "var(--text-dim)" }}>
                  Make sure the folder is set to "anyone with the link can view"
                </p>
              </div>
            </div>
          </div>
        )}

        {stepId === "numbers" && (
          <div>
            <h2 className="h-display text-2xl mb-2">The numbers</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>We run the 70% MAO rule live as you type.</p>
            <div className="grid md:grid-cols-2 gap-5">
              <NumField label="ARV (After Repair Value)" required value={form.arv} onChange={set("arv")} error={errors.arv} testid="arv-input" />
              <NumField label={isJV ? "Contract Price" : "Asking Price"} required value={form.asking_price} onChange={set("asking_price")} error={errors.asking_price} testid="asking-input" />
              <NumField label="Estimated Repairs" required value={form.repair_estimate} onChange={set("repair_estimate")} error={errors.repair_estimate} testid="repairs-input" />
              <NumField label="Your Assignment Fee" optional value={form.assignment_fee} onChange={set("assignment_fee")} testid="fee-input" />
              {isJV && (
                <div>
                  <label className="field-label">Closing Date <span className="opt">(per contract)</span></label>
                  <input
                    type="date"
                    className="input-base font-mono"
                    value={form.closing_date}
                    onChange={set("closing_date")}
                    data-testid="closing-date-input"
                  />
                </div>
              )}
            </div>

            <div className="mt-7 ai-box">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={16} style={{ color: "var(--lime)" }} />
                <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Live deal analysis</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="calc-row"><span className="lbl">MAO (70% – repairs – fee)</span><span className="val">{fmtMoney(calc.mao)}</span></div>
                <div className="calc-row"><span className="lbl">Asking vs MAO</span><span className="val" style={{ color: calc.margin >= 0 ? "var(--success)" : "var(--red)" }}>{fmtMoney(calc.margin)}</span></div>
                <div className="calc-row"><span className="lbl">Asking % of ARV</span><span className="val">{calc.ratio ? `${calc.ratio.toFixed(1)}%` : "—"}</span></div>
                <div className="calc-row"><span className="lbl">Investor Profit Est.</span><span className="val">{fmtMoney(calc.investorProfit)}</span></div>
                <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }} data-testid="profit-formula">
                  MAO = 70% of ARV − repairs − your fee. Investor profit est. = 30% of ARV − your fee.
                </p>
              </div>
            </div>
          </div>
        )}

        {stepId === "contract" && (
          <div>
            <h2 className="h-display text-2xl mb-2">Signed contract</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
                Upload your signed contract. Required for JV review. Property photos can be added via a Drive link on the Property step.
            </p>
            <div className="space-y-6">
              <div>
                <FileUpload
                  value={docs}
                  onChange={setDocs}
                  accept="application/pdf,image/*"
                  label="Contract (PDF), required"
                  testid="docs-upload"
                />
                {errors.docs && docs.length === 0 && (
                  <p className="text-xs mt-2 font-mono" style={{ color: "var(--red)" }} data-testid="docs-required-error">
                    The contract is required for JV submissions
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {stepId === "contact" && (
          <div>
            <h2 className="h-display text-2xl mb-6">Your contact</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="field-label">Your name <span className="req">*</span></label>
                <input className={`input-base ${errors.contact_name ? "error" : ""}`} value={form.contact_name} onChange={set("contact_name")} data-testid="contact-name-input" />
              </div>
              <div>
                <label className="field-label">Phone <span className="opt">(optional)</span></label>
                <input className="input-base" value={form.contact_phone} onChange={set("contact_phone")} data-testid="contact-phone-input" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Additional notes <span className="opt">(optional)</span></label>
                <textarea className="input-base" value={form.notes} onChange={set("notes")} placeholder="Anything else we should know?" data-testid="notes-textarea" />
              </div>
            </div>
          </div>
        )}

        {stepId === "review" && (
          <div>
            <h2 className="h-display text-2xl mb-6">Review & submit</h2>
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <Field k="Submission" v={isJV ? "JV Deal" : "Property Analysis"} />
              <Field k="Address" v={form.property_address} />
              <Field k="Type" v={form.property_type} />
              <Field k="Condition" v={form.condition || "—"} />
              <Field k="ARV" v={fmtMoney(form.arv)} mono />
              <Field k={isJV ? "Contract Price" : "Asking"} v={fmtMoney(form.asking_price)} mono />
              <Field k="Repairs" v={fmtMoney(form.repair_estimate)} mono />
              <Field k="Fee" v={fmtMoney(form.assignment_fee)} mono />
              {isJV && <Field k="Closing Date" v={form.closing_date || "—"} mono />}
              {isJV && <Field k="Contract" v={docs.length ? `${docs.length} file${docs.length > 1 ? "s" : ""} attached` : "Missing"} />}
              <Field k="Photos" v={form.photos_link ? "Drive link attached" : "—"} />
              {isJV && <Field k="Contact" v={form.contact_name} />}
            </div>
            <div className="ai-box">
              <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: "var(--lime)" }}>What happens next</div>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {form.submission_type === "help"
                  ? "Your coach reviews your numbers within 24 hours. You can also run an instant AI analysis on the deal page."
                  : "Your coach personally reviews JV submissions and replies in the deal thread. Expect a response within 1 business day."}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-10 pt-6 divider-faint">
          <button
            type="button"
            onClick={back}
            className="btn-ghost flex items-center gap-1"
            disabled={step === 0}
            data-testid="back-button"
          >
            <ChevronLeft size={16} /> Back
          </button>
          <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
            {step + 1} / {STEPS.length}
          </div>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} className="btn-primary flex items-center gap-1" data-testid="next-button">
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={submit} className="btn-primary flex items-center gap-2" disabled={busy} data-testid="submit-deal-button">
              {busy ? <span className="spinner" /> : <><Send size={16} /> Submit deal</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NumField({ label, required, optional, value, onChange, error, testid }) {
  return (
    <div>
      <label className="field-label">
        {label} {required && <span className="req">*</span>}{optional && <span className="opt">(optional)</span>}
      </label>
      <div className="relative">
        <span
          className="absolute top-1/2 -translate-y-1/2 font-mono pointer-events-none"
          style={{ color: "var(--text-dim)", left: "14px" }}
        >
          $
        </span>
        <input
          inputMode="numeric"
          className={`input-base font-mono ${error ? "error" : ""}`}
          style={{ paddingLeft: "28px" }}
          value={value}
          onChange={(e) => onChange({ target: { value: e.target.value.replace(/[^\d.]/g, "") } })}
          placeholder="0"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function Field({ k, v, mono }) {
  return (
    <div className="surface-card p-4">
      <div className="font-mono text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{k}</div>
      <div className={mono ? "font-mono" : ""}>{v || "—"}</div>
    </div>
  );
}
